import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  Panel,
  ReactFlowProvider,
  Handle,
  Position,
  useReactFlow,
  BaseEdge,
  getSmoothStepPath,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Custom animated edge for normal connections (solid line with moving triangle)
const AnimatedNormalEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition }) => {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge 
        id={id} 
        path={edgePath} 
        style={{ 
          stroke: '#4b5563', 
          strokeWidth: 1 
        }} 
      />
      <polygon 
        points="0,-4 8,0 0,4" 
        fill="#4b5563"
        transform="rotate(0)"
      >
        <animateMotion 
          dur="3s" 
          repeatCount="indefinite" 
          path={edgePath}
          rotate="auto"
        />
      </polygon>
    </>
  );
};

// Custom animated edge for condition connections (dashed line with moving triangle)
const AnimatedConditionEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) => {
  const label = data?.label;
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Calculate midpoint for elegant label positioning
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  return (
    <>
      <BaseEdge 
        id={id} 
        path={edgePath} 
        style={{ 
          stroke: '#fed7aa', 
          strokeWidth: 1.5,
          strokeDasharray: '8 6'
        }} 
      />
      <polygon 
        points="0,-4 8,0 0,4" 
        fill="#fed7aa"
        transform="rotate(0)"
      >
        <animateMotion 
          dur="2.5s" 
          repeatCount="indefinite" 
          path={edgePath}
          rotate="auto"
        />
      </polygon>
      {label && (
        <g>
          {/* Beautiful floating label with background */}
          <rect
            x={midX - (label.length * 3.5)}
            y={midY - 12}
            width={label.length * 7}
            height={20}
            rx="4"
            ry="4"
            fill="white"
            stroke="#fed7aa"
            strokeWidth="1"
            filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
          />
          <text
            x={midX}
            y={midY}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: '11px',
              fontWeight: '600',
              fill: '#ea580c',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >
            {label}
          </text>
        </g>
      )}
    </>
  );
};

// Register custom edge types
const edgeTypes = {
  animatedNormal: AnimatedNormalEdge,
  animatedCondition: AnimatedConditionEdge,
};

import { 
  Gear, 
  GitBranch, 
  Plus,
  Minus,
  CubeTransparent,
  Trash,
  PencilSimple,
  Link,
  LinkBreak,
  TreeStructure,
  BookOpen,
  Database,
  Wrench,
  Rows,
  Square,
  Play,
  ArrowsOutSimple,
  ArrowsInSimple,
  FileText,
  Phone,
  ChatCircle,
  Envelope,
  Globe,
  Hash,
  Chat,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';

// Enhanced helper functions for action nodes
const getActionNodeConfig = (step) => {
  // Get type and subtype directly from step properties first
  let type = step.action_type;
  let subtype = step.action_subtype;

  // If not found, check references for backward compatibility
  if (!type && step.references && step.references.length > 0) {
    const firstReference = step.references[0];
    type = firstReference.reference_type || firstReference.original_type || firstReference.type;
    subtype = firstReference.subtype || firstReference.action_type;
  }

  switch (type) {
    case 'simple_answer':
      return {
        icon: BookOpen,
        color: 'blue',
        label: 'Simple Answer',
        shouldEndFlow: false
      };

    case 'complex_answer':
      if (subtype === 'knowledge') {
        return {
          icon: Database,
          color: 'purple',
          label: 'Knowledge Answer',
          shouldEndFlow: false
        };
      } else if (subtype === 'api') {
        return {
          icon: Globe,
          color: 'purple',
          label: 'API Answer',
          shouldEndFlow: false
        };
      } else {
        return {
          icon: Database,
          color: 'purple',
          label: 'Complex Answer',
          shouldEndFlow: false
        };
      }

    case 'simple_action':
      // Handle both database values and legacy values
      switch (subtype) {
        case 'note_taking':
        case 'note':
          return {
            icon: FileText,
            color: 'yellow',
            label: 'Note Taking',
            shouldEndFlow: false
          };
        case 'transfer_call':
        case 'transfer':
          return {
            icon: Phone,
            color: 'yellow',
            label: 'Transfer Call',
            shouldEndFlow: true // Transfer calls end the flow
          };
        case 'text_message':
        case 'sms':
          return {
            icon: ChatCircle,
            color: 'yellow',
            label: 'Text Message',
            shouldEndFlow: false
          };
        case 'email':
          return {
            icon: Envelope,
            color: 'yellow',
            label: 'Email',
            shouldEndFlow: false
          };
        case 'api':
          return {
            icon: Globe,
            color: 'yellow',
            label: 'API Action',
            shouldEndFlow: false
          };
        case 'dtmf':
          return {
            icon: Hash,
            color: 'yellow',
            label: 'DTMF',
            shouldEndFlow: false
          };
        default:
          return {
            icon: Wrench,
            color: 'yellow',
            label: 'Simple Action',
            shouldEndFlow: false
          };
      }

    default:
      return {
        icon: Gear,
        color: 'blue',
        label: 'Action Step',
        shouldEndFlow: false
      };
  }
};

// Helper function to get color classes for different action types
const getColorClasses = (color) => {
  switch (color) {
    case 'blue':
      return {
        border: 'border-blue-200',
        borderSelected: 'border-blue-400',
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        textSecondary: 'text-blue-700',
        handle: '!bg-blue-500'
      };
    case 'purple':
      return {
        border: 'border-purple-200',
        borderSelected: 'border-purple-400',
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        textSecondary: 'text-purple-700',
        handle: '!bg-purple-500'
      };
    case 'yellow':
      return {
        border: 'border-amber-200',
        borderSelected: 'border-amber-400',
        bg: 'bg-amber-100',
        text: 'text-amber-600',
        textSecondary: 'text-amber-700',
        handle: '!bg-amber-500'
      };
    default:
      return {
        border: 'border-blue-200',
        borderSelected: 'border-blue-400',
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        textSecondary: 'text-blue-700',
        handle: '!bg-blue-500'
      };
  }
};

// Smart text truncation
const formatNodeText = (text) => {
  if (!text) return '';

  if (text.length <= 25) {
    return <div className="text-sm">{text}</div>;
  } else if (text.length <= 47) {
    const firstPart = text.substring(0, 25);
    const lastSpaceIndex = firstPart.lastIndexOf(' ');

    if (lastSpaceIndex > 17) {
      return (
        <div className="leading-tight text-xs">
          <div>{text.substring(0, lastSpaceIndex)}</div>
          <div>{text.substring(lastSpaceIndex + 1)}</div>
        </div>
      );
    } else {
      return (
        <div className="leading-tight text-xs">
          <div>{text.substring(0, 25)}</div>
          <div>{text.substring(25)}</div>
        </div>
      );
    }
  } else {
    const truncated = text.substring(0, 44) + '...';
    const firstPart = truncated.substring(0, 25);
    const lastSpaceIndex = firstPart.lastIndexOf(' ');

    if (lastSpaceIndex > 17) {
      return (
        <div className="leading-tight text-xs">
          <div>{truncated.substring(0, lastSpaceIndex)}</div>
          <div>{truncated.substring(lastSpaceIndex + 1)}</div>
        </div>
      );
    } else {
      return (
        <div className="leading-tight text-xs">
          <div>{truncated.substring(0, 25)}</div>
          <div>{truncated.substring(25)}</div>
        </div>
      );
    }
  }
};

// Custom Start Node
const StartNode = ({ data, selected }) => (
  <div className={`px-4 py-3 shadow-lg rounded-xl border-2 bg-white min-w-[180px] ${
    selected ? 'border-green-400' : 'border-green-200'
  }`}>
    <div className="flex items-center">
      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
        <Play className="w-4 h-4 text-green-600" />
      </div>
      <div className="flex-1">
        <div className="font-medium text-gray-900">
          {formatNodeText(data.label)}
        </div>
        <div className="text-xs text-green-600">Start Step</div>
      </div>
    </div>
    {data.onEdit && (
      <button
        onClick={() => data.onEdit(data)}
        className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center"
      >
        <PencilSimple className="w-3 h-3 mr-1" />
        Edit
      </button>
    )}

    <Handle
      type="source"
      position={Position.Right}
      id="output"
      className="w-3 h-3 !bg-green-500 !border-2 !border-white"
      style={{ top: '50%', transform: 'translateY(-50%)' }}
    />
  </div>
);

// Custom Action Node
const ActionNode = ({ data, selected }) => {
  const { icon: IconComponent, color, label, shouldEndFlow } = getActionNodeConfig(data);

  const colorClasses = getColorClasses(color);
  const isLinked = data.references && data.references.length > 0;

  return (
    <div className={`relative px-4 py-3 shadow-lg rounded-xl border-2 bg-white min-w-[180px] ${
      selected ? colorClasses.borderSelected : colorClasses.border
    }`}>
      <div className="flex items-center">
        <div className={`w-8 h-8 ${colorClasses.bg} rounded-lg flex items-center justify-center mr-3`}>
          <IconComponent className={`w-4 h-4 ${colorClasses.text}`} />
        </div>
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            {formatNodeText(data.label)}
          </div>
          <div className={`text-xs ${colorClasses.text}`}>{label}</div>
        </div>
      </div>

      {data.onEdit && (
        <button
          onClick={() => data.onEdit(data)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center"
        >
          <PencilSimple className="w-3 h-3 mr-1" />
          Edit
        </button>
      )}

      {/* Linked/Unlinked indicator in bottom right */}
      <div className="absolute bottom-2 right-2">
        {isLinked ? (
          <Link className={`w-3 h-3 ${colorClasses.text}`} />
        ) : (
          <LinkBreak className={`w-3 h-3 ${colorClasses.text} opacity-40`} />
        )}
      </div>

      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className={`w-3 h-3 ${colorClasses.handle} !border-2 !border-white`}
        style={{ top: '50%', transform: 'translateY(-50%)' }}
      />

      {/* Only show output handle if this action doesn't end the flow */}
      {!shouldEndFlow && (
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className={`w-3 h-3 ${colorClasses.handle} !border-2 !border-white`}
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        />
      )}
    </div>
  );
};

// Custom Condition Node
const ConditionNode = ({ data, selected }) => (
  <div className={`px-4 py-3 shadow-lg rounded-xl border-2 bg-white min-w-[180px] ${
    selected ? 'border-orange-400' : 'border-orange-200'
  }`}>
    <div className="flex items-center">
      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
        <GitBranch className="w-4 h-4 text-orange-600" />
      </div>
      <div className="flex-1">
        <div className="font-medium text-gray-900">
          {formatNodeText(data.label)}
        </div>
        <div className="text-xs text-orange-600">Condition Step</div>
      </div>
    </div>

    <div className="mt-2 space-y-1">
      {data.config?.prompt && (
        <div className="flex items-center text-xs text-orange-600">
          {data.config.prompt_type === 'multiple_choice' ? (
            <Rows className="w-3 h-3 mr-1" />
          ) : (
            <Chat className="w-3 h-3 mr-1" />
          )}
          Prompt: {data.config.prompt_type === 'multiple_choice' ? 'Multiple Choice' : 'Open Text'}
        </div>
      )}
      {data.conditions && data.conditions.length > 0 && (
        <div className="flex items-center text-xs text-orange-700">
          <TreeStructure className="w-3 h-3 mr-1" />
          {data.conditions.length} condition{data.conditions.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>

    {data.onEdit && (
      <button
        onClick={() => data.onEdit(data)}
        className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center"
      >
        <PencilSimple className="w-3 h-3 mr-1" />
        Edit
      </button>
    )}

    <Handle
      type="target"
      position={Position.Left}
      id="input"
      className="w-3 h-3 !bg-orange-500 !border-2 !border-white"
      style={{ top: '50%', transform: 'translateY(-50%)' }}
    />

    <Handle
      type="source"
      position={Position.Right}
      id="output"
      className="w-3 h-3 !bg-orange-500 !border-2 !border-white"
      style={{ top: '50%', transform: 'translateY(-50%)' }}
    />
  </div>
);

// Custom End Node
const EndNode = ({ data, selected }) => (
  <div className={`px-4 py-3 shadow-lg rounded-xl border-2 bg-white min-w-[180px] ${
    selected ? 'border-purple-400' : 'border-purple-200'
  }`}>
    <div className="flex items-center">
      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
        <Square className="w-4 h-4 text-purple-600" />
      </div>
      <div className="flex-1">
        <div className="font-medium text-gray-900">
          {formatNodeText(data.label)}
        </div>
        <div className="text-xs text-purple-600">End Step</div>
      </div>
    </div>
    {data.onEdit && (
      <button
        onClick={() => data.onEdit(data)}
        className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center"
      >
        <PencilSimple className="w-3 h-3 mr-1" />
        Edit
      </button>
    )}

    <Handle
      type="target"
      position={Position.Left}
      id="input"
      className="w-3 h-3 !bg-purple-500 !border-2 !border-white"
      style={{ top: '50%', transform: 'translateY(-50%)' }}
    />
  </div>
);

// **NEW: Ghost Add Node (same card style as regular nodes)**
const GhostNode = ({ data }) => {
  const [showMenu, setShowMenu] = useState(false);

  // Check if this ghost node is after a condition node
  const isAfterConditionNode = data.parentNodeType === 'condition';

  const handleClick = () => {
    if (isAfterConditionNode && data.onEditParent) {
      // For condition nodes, open the parent condition node for editing
      data.onEditParent();
    } else {
      // For other nodes, show the add step menu
      setShowMenu(!showMenu);
    }
  };

  return (
    <div className="relative">
      <div 
        className="px-4 py-3 shadow-lg rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-all cursor-pointer min-w-[180px]"
        onClick={handleClick}
      >
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
            {isAfterConditionNode ? (
              <Gear className="w-4 h-4 text-gray-400" />
            ) : (
              <Plus className="w-4 h-4 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            {isAfterConditionNode ? (
              <>
                <div className="text-sm text-gray-500">Configure Conditions</div>
                <div className="text-xs text-gray-400">Click to set up paths</div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-500">Add Step</div>
                <div className="text-xs text-gray-400">Click to add</div>
              </>
            )}
          </div>
        </div>
        {/* Invisible spacer to match StartNode Edit button exactly */}
        <div className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center opacity-0 pointer-events-none">
          <PencilSimple className="w-3 h-3 mr-1" />
          Edit
        </div>
      </div>

      {/* Add Step Menu - only show for non-condition nodes */}
      {showMenu && !isAfterConditionNode && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-2 z-[100] min-w-[180px]">
          <div className="space-y-0">
            <button
              onClick={() => {
                data.onAddStep('action', data.parentId);
                setShowMenu(false);
              }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-blue-50 flex items-center text-sm"
            >
              <Gear className="w-4 h-4 text-blue-600 mr-2" />
              Action Step
            </button>
            <div className="h-px bg-gray-200 mx-2"></div>
            <button
              onClick={() => {
                data.onAddStep('condition', data.parentId);
                setShowMenu(false);
              }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-orange-50 flex items-center text-sm"
            >
              <TreeStructure className="w-4 h-4 text-orange-600 mr-2" />
              Condition Step
            </button>
            <div className="h-px bg-gray-200 mx-2"></div>
            <button
              onClick={() => {
                data.onAddStep('end', data.parentId);
                setShowMenu(false);
              }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-purple-50 flex items-center text-sm"
            >
              <Square className="w-4 h-4 text-purple-600 mr-2" />
              End Step
            </button>
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="w-3 h-3 !bg-gray-400 !border-2 !border-white"
        style={{ top: '50%', transform: 'translateY(-50%)' }}
      />
    </div>
  );
};

const nodeTypes = {
  startNode: StartNode,
  actionNode: ActionNode,
  conditionNode: ConditionNode,
  endNode: EndNode,
  ghostNode: GhostNode,
};

// Positioning constants - optimized for visual clarity
const NODE_WIDTH = 280;
const NODE_HEIGHT = 120;
const BRANCH_VERTICAL_SPACING = 200;
const GHOST_OFFSET = 280; // Same as NODE_WIDTH for consistent line lengths
const CONDITION_BRANCH_OFFSET = 350; // Enhanced spacing for condition branches
const SEQUENTIAL_OFFSET = 300; // Tighter spacing for sequential steps

const WorkflowFlow = ({ 
  steps = [], 
  onStepEdit,
  onAddStep,
  onDeleteStep,
  className = ""
}) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  // **NEW: Smart positioning algorithm**
  const calculateNodePosition = (stepIndex, parentPosition = null, isGhost = false) => {
    if (isGhost && parentPosition) {
      return {
        x: parentPosition.x + GHOST_OFFSET,
        y: parentPosition.y
      };
    }

    if (parentPosition) {
      return {
        x: parentPosition.x + SEQUENTIAL_OFFSET, // Use sequential offset instead of NODE_WIDTH
        y: parentPosition.y
      };
    }

    return { x: 100, y: 100 + (stepIndex * 150) };
  };

  // **ENHANCED BRANCH POSITIONING** - Handles complex condition branch layouts
  const calculateBranchPositions = (steps) => {
    const positionMap = new Map();
    const processedSteps = new Set();

    // Build condition branches map for O(1) lookups
    const conditionBranches = new Map();
    steps.forEach(step => {
      if (step.type === 'condition' && step.conditions) {
        const targetIds = step.conditions
          .map(c => c.target_step_id)
          .filter(id => id && steps.find(s => s.id === id));
        if (targetIds.length > 0) {
          conditionBranches.set(step.id, targetIds);
        }
      }
    });

    // Helper function to find available Y coordinate to prevent overlaps
    const findAvailableY = (preferredY, xCoordinate) => {
      const tolerance = NODE_HEIGHT + 20; // Minimum spacing between nodes
      let adjustedY = preferredY;
      let attempts = 0;

      while (attempts < 50) { // Prevent infinite loops
        let hasConflict = false;

        // Check if this Y coordinate conflicts with existing nodes at similar X
        for (const [stepId, position] of positionMap) {
          if (Math.abs(position.x - xCoordinate) < NODE_WIDTH + 50) { // Same column vicinity
            if (Math.abs(position.y - adjustedY) < tolerance) {
              hasConflict = true;
              break;
            }
          }
        }

        if (!hasConflict) {
          return adjustedY;
        }

        // Try above and below the preferred position alternately
        const offset = Math.ceil(attempts / 2) * tolerance;
        adjustedY = attempts % 2 === 0 ? preferredY + offset : preferredY - offset;
        attempts++;
      }

      return adjustedY; // Return best attempt if no perfect spot found
    };

    const processStep = (step, parentPos = null, branchIndex = 0, totalBranches = 1) => {
      if (processedSteps.has(step.id)) return positionMap.get(step.id);

      let position;
      if (!parentPos) {
        // Start node or orphaned step - use horizontal layout for better flow
        const rootIndex = Array.from(processedSteps).length;
        position = { x: 100, y: 100 + (rootIndex * 150) };
      } else {
        // **IMPROVED CONDITION BRANCH SPACING** - Only use enhanced spacing for actual condition branches
        const isConditionBranch = steps.some(s => 
          s.type === 'condition' && 
          s.conditions && 
          s.conditions.some(c => c.target_step_id === step.id)
        );

        // **FIX: Use different spacing based on relationship type**
        const horizontalOffset = isConditionBranch ? CONDITION_BRANCH_OFFSET : SEQUENTIAL_OFFSET;
        const targetX = parentPos.x + horizontalOffset;

        // **IMPROVED VERTICAL POSITIONING** - Always calculate new positions
        if (totalBranches > 1) {
          // Enhanced vertical spacing for multiple branches
          const enhancedSpacing = Math.max(BRANCH_VERTICAL_SPACING, totalBranches * 80);
          const baseY = parentPos.y - ((totalBranches - 1) * enhancedSpacing) / 2;
          const preferredY = baseY + (branchIndex * enhancedSpacing);

          // Find available Y coordinate to prevent overlaps
          const availableY = findAvailableY(preferredY, targetX);

          position = {
            x: targetX,
            y: availableY
          };
        } else {
          // Single connection - keep on same horizontal line for clean flow
          position = {
            x: targetX,
            y: parentPos.y
          };
        }
      }

      positionMap.set(step.id, position);
      processedSteps.add(step.id);

      // Process condition branch targets first (higher priority)
      if (conditionBranches.has(step.id)) {
        // This is a condition step with branches
        const targetIds = conditionBranches.get(step.id);
        targetIds.forEach((targetId, index) => {
          const targetStep = steps.find(s => s.id === targetId);
          if (targetStep && !processedSteps.has(targetId)) {
            processStep(targetStep, position, index, targetIds.length);
          }
        });
      }

      // Process steps that have this step as parent (non-condition relationships)
      const childSteps = steps.filter(s => s.parentId === step.id && !processedSteps.has(s.id));
      childSteps.forEach((childStep, index) => {
        // **FIX: For sequential steps, use consistent spacing**
        processStep(childStep, position, index, childSteps.length);
      });

      return position;
    };

    // **IMPROVED PROCESSING ORDER** - Process steps in a logical flow
    // 1. Start with start nodes first
    const startSteps = steps.filter(s => s.type === 'start');
    if (startSteps.length > 0) {
      startSteps.forEach((step, index) => {
        const startPosition = { x: 100, y: 100 + (index * 200) };
        positionMap.set(step.id, startPosition);
        processedSteps.add(step.id);

        // Process children of start step
        const childSteps = steps.filter(s => s.parentId === step.id);
        childSteps.forEach((childStep, childIndex) => {
          processStep(childStep, startPosition, childIndex, childSteps.length);
        });
      });
    }

    // 2. Process any remaining unprocessed steps in order
    const remainingSteps = steps.filter(s => !processedSteps.has(s.id));
    remainingSteps.forEach((step, index) => {
      if (!processedSteps.has(step.id)) {
        // For orphaned steps, place them in a vertical sequence
        const orphanPosition = { x: 100, y: 100 + ((processedSteps.size + index) * 150) };
        processStep(step, orphanPosition, 0, 1);
      }
    });

    return positionMap;
  };

  // Helper function to check if a step is a condition target
  const isConditionTarget = (stepId, steps) => {
    return steps.some(step => 
      step.type === 'condition' && 
      step.conditions && 
      step.conditions.some(c => c.target_step_id === stepId)
    );
  };

  // **IMPROVED: Check if a step should have a ghost node**
  const shouldHaveGhostNode = (step, allSteps) => {
    // End nodes never have ghost nodes
    if (step.type === 'end') return false;

    // Check if this action ends the flow (like transfer calls)
    if (step.type === 'action') {
      const { shouldEndFlow } = getActionNodeConfig(step);
      if (shouldEndFlow) return false; // No ghost node for flow-ending actions

      const hasDirectChildren = allSteps.some(s => s.parentId === step.id);
      const hasConditionTargets = step.conditions && step.conditions.length > 0;
      return !hasDirectChildren && !hasConditionTargets;
    }

    // Start nodes always have ghost nodes if they don't connect to anything
    if (step.type === 'start') {
      const hasDirectChildren = allSteps.some(s => s.parentId === step.id);
      const hasConditionTargets = step.conditions && step.conditions.length > 0;
      return !hasDirectChildren && !hasConditionTargets;
    }

    // **IMPROVED CONDITION NODE LOGIC** - Show ghost node for configuration
    if (step.type === 'condition') {
      // Always show ghost node for condition nodes to allow configuration
      // This helps with the UX when users are setting up decision branches
      const hasConfiguredConditions = step.conditions && 
        step.conditions.length > 0 && 
        step.conditions.some(c => c.target_step_id && c.expected_value);

      // Show ghost node if no properly configured conditions exist
      return !hasConfiguredConditions;
    }

    return false;
  };

  // **NEW: Generate nodes with proper connections and positioning**
  const generateNodesAndEdges = useMemo(() => {
    const nodes = [];
    const edges = [];

    if (steps.length === 0) {
      // Create initial start node with ghost node
      const startId = 'start-node';
      const ghostId = 'ghost-after-start';
      const startPosition = calculateNodePosition(0);

      nodes.push({
        id: startId,
        type: 'startNode',
        position: startPosition,
        data: {
          label: 'Start Workflow',
          onEdit: () => onStepEdit({ id: startId, name: 'Start Workflow', type: 'start' })
        }
      });

      nodes.push({
        id: ghostId,
        type: 'ghostNode',
        position: calculateNodePosition(0, startPosition, true),
        data: {
          parentId: startId,
          parentNodeType: 'start', // Pass the parent node type for start node
          onAddStep: (type, parentId) => {
            const newStepId = onAddStep(type, parentId);
            return newStepId;
          },
                      onEditParent: () => onStepEdit({ id: startId, name: 'Start Workflow', type: 'start' }) // Pass the parent node edit handler
        }
      });

      edges.push({
        id: `edge-${startId}-${ghostId}`,
        source: startId,
        target: ghostId,
        sourceHandle: 'output',
        targetHandle: 'input',
        type: 'smoothstep', // Smoother curves for better visual flow
        style: { 
          stroke: '#d1d5db', // Darker gray for better ghost line visibility
          strokeWidth: 2,
          strokeDasharray: '6 4', // Light dashes for ghost indication
          opacity: 0.6 
        }
      });

      return { nodes, edges };
    }

    // **FORCE RECALCULATION** - Always recalculate positions for all workflows
    const positionMap = calculateBranchPositions(steps);

    // Create real nodes with calculated positions
    steps.forEach((step) => {
      const position = positionMap.get(step.id);

      // **ENSURE POSITION EXISTS** - Fallback to default if somehow missing
      const finalPosition = position || { x: 100 + (nodes.length * 200), y: 100 };

      let nodeType;
      switch (step.type) {
        case 'start':
          nodeType = 'startNode';
          break;
        case 'action':
          nodeType = 'actionNode';
          break;
        case 'condition':
          nodeType = 'conditionNode';
          break;
        case 'end':
          nodeType = 'endNode';
          break;
        default:
          nodeType = 'actionNode';
      }

      nodes.push({
        id: step.id,
        type: nodeType,
        position: finalPosition, // Use calculated position
        data: {
          ...step, // Pass all step properties
          label: step.name,
          onEdit: onStepEdit // Passed from WorkflowModal's props
        }
      });

      // Add ghost node if this step should have one
      if (shouldHaveGhostNode(step, steps)) {
        const ghostId = `ghost-after-${step.id}`;
        const ghostPosition = calculateNodePosition(0, finalPosition, true);

        nodes.push({
          id: ghostId,
          type: 'ghostNode',
          position: ghostPosition,
          data: {
            parentId: step.id,
            parentNodeType: step.type, // Pass the parent node type
            onAddStep: (type, parentId) => {
              const newStepId = onAddStep(type, parentId);
              return newStepId;
            },
            onEditParent: () => onStepEdit(step) // Pass the parent node edit handler
          }
        });

        // Edge from step to ghost - Elegant dashed style
        edges.push({
          id: `edge-${step.id}-${ghostId}`,
          source: step.id,
          target: ghostId,
          sourceHandle: 'output',
          targetHandle: 'input',
          type: 'smoothstep', // Smooth curves
          style: { 
            stroke: '#d1d5db', // Darker gray for better ghost line visibility
            strokeWidth: 2,
            strokeDasharray: '6 4', // Light dashes for ghost indication
            opacity: 0.6 
          },
          markerEnd: { 
            type: 'arrowclosed', 
            color: '#d1d5db',
            width: 16,
            height: 16,
            className: 'animated-arrow'
          }
        });
      }
    });

    // Create edges between real steps based on parent relationships
    steps.forEach(step => {
      if (step.parentId) {
        const parentStep = steps.find(s => s.id === step.parentId);
        if (parentStep) {
          // Only create parent-child edge if this step is NOT a condition target
          const isConditionTarget = steps.some(s => 
            s.type === 'condition' && 
            s.conditions && 
            s.conditions.some(c => c.target_step_id === step.id)
          );

          if (!isConditionTarget) {
            edges.push({
              id: `parent-${step.parentId}-${step.id}`,
              source: step.parentId,
              target: step.id,
              sourceHandle: 'output',
              targetHandle: 'input',
              type: 'animatedNormal'
            });
          }
        }
      }
    });

    // Create edges for condition-based connections - Premium styling
    steps.forEach(step => {
      if (step.conditions && step.conditions.length > 0) {
        step.conditions.forEach(condition => {
          if (condition.target_step_id) {
            const targetStep = steps.find(s => s.id === condition.target_step_id);
            if (targetStep) {
              let label = condition.expected_value || 'condition';
              if (label === '_default_fallback') label = 'other';

              edges.push({
                id: `condition-${step.id}-${condition.target_step_id}`,
                source: step.id,
                target: condition.target_step_id,
                sourceHandle: 'output',
                targetHandle: 'input',
                type: 'animatedCondition',
                data: { label }
              });
            }
          }
        });
      }
    });

    return { nodes, edges };
  }, [steps, onStepEdit, onAddStep]);

  const [nodes, setNodes, onNodesChange] = useNodesState(generateNodesAndEdges.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(generateNodesAndEdges.edges);
  const { fitView: reactFlowFitView, zoomIn: reactFlowZoomIn, zoomOut: reactFlowZoomOut } = useReactFlow();

  // Initial fit view on mount
  React.useEffect(() => {
    setTimeout(() => {
      reactFlowFitView({ padding: 0.2, duration: 300 });
    }, 100); // Small delay to ensure component is fully mounted
  }, []); // Only run once on mount

  // Update nodes and edges when steps change
  React.useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = generateNodesAndEdges;
    setNodes(newNodes);
    setEdges(newEdges);

    // Auto-fit view whenever nodes/edges change (like Draw3D button)
    setTimeout(() => {
      reactFlowFitView({ padding: 0.2, duration: 300 });
    }, 50); // Small delay to ensure DOM is updated
  }, [generateNodesAndEdges, setNodes, setEdges, reactFlowFitView]);

  // **NEW: Handle node changes (no dragging allowed)**
  const handleNodesChange = useCallback((changes) => {
    // Filter out position changes to prevent dragging
    const filteredChanges = changes.filter(change => change.type !== 'position');
    onNodesChange(filteredChanges);
  }, [onNodesChange]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`${className} ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'relative'} h-full`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false} // Disable node dragging
        nodesConnectable={false} // Disable manual connections
        elementsSelectable={true} // Keep selection for editing
        nodeOrigin={[0, 0]} // Use top-left origin for predictable positioning
        attributionPosition="bottom-left"
      >
        {/* Custom Controls */}
        <div className="absolute bottom-8 left-8 flex flex-col space-y-2 z-10">
          <button
            onClick={() => reactFlowZoomIn()}
            className="w-8 h-8 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex items-center justify-center"
            title="Zoom In"
          >
            <Plus className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => reactFlowZoomOut()}
            className="w-8 h-8 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex items-center justify-center"
            title="Zoom Out"
          >
            <Minus className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => reactFlowFitView({ padding: 0.2, duration: 300 })}
            className="w-8 h-8 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex items-center justify-center"
            title="Fit View"
          >
            <CubeTransparent className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="w-8 h-8 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex items-center justify-center"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <ArrowsInSimple className="w-4 h-4 text-gray-600" />
            ) : (
              <ArrowsOutSimple className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>

        <MiniMap 
          style={{
            height: 80,
            width: 120,
          }}
          zoomable
          pannable
          className="!bottom-8 !right-8"
        />

        <Background variant="dots" gap={20} size={1} />
      </ReactFlow>
    </div>
  );
};

// Legacy helper function (kept for backward compatibility)
const getBrainItemIcon = (reference) => {
  if (!reference) return Gear;

  const type = reference.original_type || reference.type;
  switch (type) {
    case 'simple_answer':
      return BookOpen;
    case 'complex_answer':
      return Database;
    case 'simple_action':
      return Wrench;
    default:
      return Gear;
  }
};

export default function WorkflowFlowWrapper(props) {
  return (
    <ReactFlowProvider>
      <WorkflowFlow {...props} />
    </ReactFlowProvider>
  );
} 